import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { NewTrialType } from "../types";
import bioApplicationIcon from "./icons/bioApplication.svg";
import seedRateIcon from "./icons/seedRate.svg";
import seedProductIcon from "./icons/seedProduct.svg";
import plotSizeIcon from "./icons/plotSize.svg";
import plotNameIcon from "./icons/plotName.svg";
import fertilizerProductNameIcon from "./icons/fertilizerProductName.svg";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { asNonEmpty } from "../../../shared/utils/stringUtils";
import {
  formatNumber,
  formatWithTwoDecimals,
} from "../../../shared/utils/numberFormatting";
import { calculatePlotAreaHa } from "../../../shared/utils/areaCalculation";
import "./SubplotDetailsCard.less";

interface SubplotDetailsCardProps {
  trailData: NewTrialType;
  properties: any;
  ratesAndDosage: any;
  trialProtocolData: any;
  current?: number;
}

const DetailSection: React.FC<{
  title: string;
  color: string;
  children: React.ReactNode;
}> = ({ title, color, children }) => (
  <div className={`detail-section ${color}`}>
    <h4>{title}</h4>
    {children}
  </div>
);

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string | number;
}> = ({ icon, label, value }) => (
  <div className="detail-item">
    <div className="label">
      <img src={icon} alt={`${label} Icon`} />
      <span>{label}</span>
    </div>
    <span className="value">{value}</span>
  </div>
);

const PlotDetails: React.FC<{
  plot: any;
  subPlotIndex: number;
  plotIndex: number;
  properties: any;
  trailData: any;
}> = ({ plot, subPlotIndex, properties, plotIndex, trailData }) => {
  const { t } = useTranslation();

  return (
    <div className="plot-details">
      <h3>
        {trailData.trial_plots?.some((plot: any) => plot.plot?.isStripPlot)
          ? formatTranslation(t("plotDetails.strip"))
          : formatTranslation(t("plotDetails.plotGroup"))}{" "}
        {trailData.trial_plots?.some((plot: any) => plot.plot?.isStripPlot)
          ? ""
          : String.fromCharCode(65 + plotIndex) + " "}
        {properties?.isReplicant
          ? formatTranslation(t("plotDetails.replicant"))
          : ""}
      </h3>
      <DetailItem
        icon={plotNameIcon}
        label={
          trailData.trial_plots?.some((plot: any) => plot.plot?.isStripPlot)
            ? formatTranslation(t("plotDetails.stripName"))
            : formatTranslation(t("plotDetails.plotName"))
        }
        value={subPlotIndex + 1}
      />
      {trailData.trial_plots?.some((plot: any) => plot.plot?.isStripPlot) ? (
        <>
          {(() => {
            // Get actual strip data from stripsData based on the current subPlotIndex
            const stripDetails = trailData.stripsData?.stripDetails || [];
            let stripData = null;

            // Use subPlotIndex to get the correct strip data since subPlotIndex represents the current strip
            if (typeof subPlotIndex === "number" && subPlotIndex >= 0) {
              // First try to find by index property that matches subPlotIndex
              stripData = stripDetails.find(
                (strip: any) => strip.index === subPlotIndex
              );

              // If not found by index property, use array index as fallback
              if (!stripData && stripDetails[subPlotIndex]) {
                stripData = stripDetails[subPlotIndex];
              }
            }

            const stripLength = Number(
              stripData?.stripLength ?? plot?.plot?.stripLength ?? 0
            );
            const stripWidth = Number(
              stripData?.stripWidth ?? plot?.plot?.stripWidth ?? 0
            );

            // Calculate plot_id for strip
            const plotId = `${String(plot?.plot?.id ?? "")}-0-${subPlotIndex}`;

            // Use common area calculation function
            const areaHa = calculatePlotAreaHa(
              plotId,
              trailData.trial_plots,
              trailData.stripsData,
              plotIndex
            );

            return (
              <>
                <DetailItem
                  icon={plotSizeIcon}
                  label={formatTranslation(t("plotDetails.stripDimensions"))}
                  value={`${stripLength.toFixed(1)}m × ${stripWidth.toFixed(
                    1
                  )}m`}
                />
                <DetailItem
                  icon={plotSizeIcon}
                  label={formatTranslation(t("plotDetails.stripArea"))}
                  value={`${areaHa.toFixed(2)} ha`}
                />
              </>
            );
          })()}
        </>
      ) : (
        <DetailItem
          icon={plotSizeIcon}
          label={formatTranslation(t("plotDetails.plotSize"))}
          value={`${Number(plot?.plot?.column_size ?? 0).toFixed(
            2
          )}m × ${Number(plot?.plot?.row_size ?? 0).toFixed(2)}m`}
        />
      )}
    </div>
  );
};

export const SubplotDetailsCard: React.FC<SubplotDetailsCardProps> = ({
  trailData,
  properties,
  ratesAndDosage,
  trialProtocolData,
  current = 1,
}) => {
  const { t } = useTranslation();
  const [trailPlotIndex, setTrailPlotIndex] = useState<number | null>(null);
  const [subPlotIndex, setSubPlotIndex] = useState<number | null>(null);
  const isHovered = useRef<boolean>(false);

  // Follow exact same logic as Plots/index.tsx for seed unit
  const measureUnitFromTrialInfo =
    trailData?.trial_plots?.[0]?.properties?.seeds?.measure_unit ?? null;
  const measureUnitFromProtocol =
    trialProtocolData?.product_details?.seeds?.measure_unit;

  const seedUnit =
    asNonEmpty(ratesAndDosage?.unit_of_rate) ??
    asNonEmpty(measureUnitFromTrialInfo) ??
    asNonEmpty(measureUnitFromProtocol) ??
    "seeds/ha";

  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastEnterEventRef = useRef<{ index: number; subIndex: number } | null>(
    null
  );

  const handleCustomEvent = useCallback((event: any): void => {
    const { index, subIndex, type } = event.detail;

    if (type === "enter") {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }
      setTrailPlotIndex(index);
      setSubPlotIndex(subIndex);
      lastEnterEventRef.current = { index, subIndex };
    } else if (type === "leave") {
      if (
        lastEnterEventRef.current &&
        lastEnterEventRef.current.index === index &&
        lastEnterEventRef.current.subIndex === subIndex
      ) {
        leaveTimeoutRef.current = setTimeout(() => {
          if (!isHovered.current) {
            setTrailPlotIndex(null);
            setSubPlotIndex(null);
            lastEnterEventRef.current = null;
          }
        }, 2000);
      }
    } else if (type === "leaveAll") {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }
      setTrailPlotIndex(null);
      setSubPlotIndex(null);
      lastEnterEventRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (current <= 1) {
      return;
    }
    window.addEventListener("subplotMouseOver", handleCustomEvent);

    return () => {
      window.removeEventListener("subplotMouseOver", handleCustomEvent);
    };
  }, [current, handleCustomEvent]);

  const plot = trailData.trial_plots[trailPlotIndex ?? 0];
  const ratesAndDosages =
    ratesAndDosage?.rates_and_dosages ??
    plot?.properties?.seeds?.rates_and_dosages;

  if (
    trailPlotIndex != null &&
    subPlotIndex != null &&
    trailPlotIndex >= 0 &&
    subPlotIndex >= 0
  ) {
    const currentRatesAndDosages =
      ratesAndDosages?.[trailPlotIndex]?.[subPlotIndex];
    return (
      <div
        id="subPlotDetailsCard"
        onMouseOver={() => {
          isHovered.current = true;
        }}
        onMouseLeave={() => {
          isHovered.current = false;

          const myEvent = new CustomEvent("subplotMouseOver", {
            detail: {
              message: `focus removed from plot ${trailPlotIndex} ${subPlotIndex}`,
              type: "leaveAll",
              index: trailPlotIndex,
              subIndex: subPlotIndex,
            },
          });
          window.dispatchEvent(myEvent);
        }}
      >
        <div className="details-container">
          <PlotDetails
            plot={plot}
            plotIndex={trailPlotIndex}
            subPlotIndex={subPlotIndex}
            properties={properties}
            trailData={trailData}
          />
          {trialProtocolData?.protocol_type &&
            trialProtocolData?.protocol_type?.includes?.("seed") && (
              <DetailSection
                title={formatTranslation(t("plotDetails.seeds"))}
                color="seeds"
              >
                {currentRatesAndDosages && (
                  <>
                    <DetailItem
                      icon={seedProductIcon}
                      label={formatTranslation(t("plotDetails.product"))}
                      value={currentRatesAndDosages.variety}
                    />
                    <DetailItem
                      icon={seedRateIcon}
                      label={`${formatTranslation(
                        t("plotDetails.seedRate")
                      )} (${String(seedUnit)})`}
                      value={formatNumber(
                        Number(currentRatesAndDosages?.rate ?? 0),
                        seedUnit,
                        "rate"
                      )}
                    />
                  </>
                )}
              </DetailSection>
            )}
          {trialProtocolData?.protocol_type &&
            trialProtocolData?.protocol_type?.includes?.("biological") && (
              <DetailSection
                title={formatTranslation(t("plotDetails.biologicals"))} // add cp and bio name */}
                color="biologicals"
              >
                {currentRatesAndDosages && (
                  <>
                    {currentRatesAndDosages?.bioTreatement?.map(
                      (
                        treatment: {
                          treated: boolean;
                          selected_treatment?: string;
                        },
                        index: number
                      ) => (
                        <DetailItem
                          key={index}
                          icon={bioApplicationIcon}
                          label={`${formatTranslation(
                            t("plotDetails.application")
                          )} ${index + 1}:`}
                          value={
                            treatment.treated
                              ? `${formatTranslation(
                                  t("plotDetails.tretment.name")
                                )} ${
                                  treatment.selected_treatment
                                    ? treatment.selected_treatment
                                    : ""
                                }`
                              : formatTranslation(t("plotDetails.untreated"))
                          }
                        />
                      )
                    )}
                  </>
                )}
              </DetailSection>
            )}
          {trialProtocolData?.protocol_type &&
            trialProtocolData?.protocol_type?.includes?.("nutrient") && (
              <DetailSection
                title={formatTranslation(t("plotDetails.fertilisers"))}
                color="fertilisers"
              >
                {currentRatesAndDosages && (
                  <>
                    {ratesAndDosage?.standard_fertilisers?.map(
                      (
                        fertiliser: {
                          fertiliser: string;
                          rate: number;
                          unit_of_rate?: string;
                        },
                        index: number
                      ) => {
                        const calculatedAmount =
                          (Number(fertiliser.rate) / 100) *
                          Number(currentRatesAndDosages.fertiliser_rate);
                        const unit = fertiliser.unit_of_rate ?? "kg/ha";

                        return (
                          <DetailItem
                            key={index}
                            icon={fertilizerProductNameIcon}
                            label={`${fertiliser.fertiliser} (${unit})`}
                            value={formatWithTwoDecimals(calculatedAmount)}
                          />
                        );
                      }
                    )}
                  </>
                )}
              </DetailSection>
            )}
        </div>
      </div>
    );
  }

  return null;
};
